import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'motion/react';
import { Calendar, Tag, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import ScrollToTop from '../components/ScrollToTop';
import SEO from '../components/SEO';
import { FadeIn } from '../components/FadeIn';
import { getPostBySlug } from '../utils/blog';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={post.title}
        description={post.description}
        url={`/blog/${post.slug}`}
        type="article"
        image={post.thumbnail}
        article={{
          publishedTime: post.date,
          tags: post.tags,
        }}
      />
      <Header />

      <article className="pt-32 pb-32 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <FadeIn>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              블로그로 돌아가기
            </Link>
          </FadeIn>

          {/* Header */}
          <FadeIn delay={100}>
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6 text-sm text-zinc-500">
                <span className="px-2.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs font-medium">
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(post.date).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>

              <motion.h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {post.title}
              </motion.h1>

              <p className="text-lg text-zinc-400 leading-relaxed">
                {post.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-6">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-zinc-900/50 text-zinc-500 rounded-full border border-zinc-800/50"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Divider */}
          <div className="w-full h-px bg-zinc-800 mb-12" />

          {/* Content */}
          <FadeIn delay={200}>
            <div className="prose prose-invert prose-zinc max-w-none
              prose-headings:font-bold prose-headings:text-white
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-zinc-400 prose-p:leading-relaxed
              prose-li:text-zinc-400
              prose-strong:text-white
              prose-code:text-zinc-300 prose-code:bg-zinc-900 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-zinc-950 prose-pre:border prose-pre:border-zinc-800 prose-pre:rounded-xl
              prose-a:text-zinc-300 prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-white
              prose-hr:border-zinc-800
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content}
              </ReactMarkdown>
            </div>
          </FadeIn>

          {/* Bottom navigation */}
          <div className="mt-16 pt-8 border-t border-zinc-800">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              다른 프로젝트 보기
            </Link>
          </div>
        </div>
      </article>

      <ScrollToTop />
    </div>
  );
}
